<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { LogOut, Plus, Users } from 'lucide-vue-next';

import SavedAuditList from './sidebar/SavedAuditList.vue';
import { Button } from '@/shared/ui/button';

import { ALL_ITEMS } from '../data/rubric';
import { assessedCount, buildDemoAudit } from '../tools/audit-helpers';
import { useAuditsStore } from '@/stores/audits';
import { useAuthStore } from '@/stores/auth';

const store = useAuditsStore();
const auth = useAuthStore();
const route = useRoute();
const router = useRouter();

async function logout(): Promise<void> {
  await auth.logout();
  await router.push({ name: 'Login' });
}

const NAV = [
  { step: 'collect', num: '01', label: 'Collect', to: '/collect' },
  { step: 'audit', num: '02', label: 'Audit', to: '/audit' },
  { step: 'report', num: '03', label: 'Report', to: '/report' },
] as const;

const figBadge = computed(() => store.current?.figures.length ?? 0);
const auditBadge = computed(() => (store.current ? assessedCount(store.current) : 0) + '/' + ALL_ITEMS.length);

const isUsersActive = computed(() => route.path.startsWith('/users'));

async function newAudit(): Promise<void> {
  await store.create();
  router.push('/collect');
}

async function loadDemo(): Promise<void> {
  const a = buildDemoAudit(store.blankAudit());
  await store.addDemo(a);
  router.push('/report');
}
</script>

<template>
  <aside
    data-testid="app-sidebar"
    class="flex flex-col overflow-y-auto border-r bg-card max-[820px]:hidden print:hidden"
  >
    <div class="border-b px-[22px] pb-[18px] pt-[22px]">
      <div class="flex items-center gap-2.5">
        <div
          class="grid size-[30px] flex-none place-items-center rounded-sm bg-gradient-to-br from-primary to-primary-dim shadow-[0_4px_14px_rgba(255,93,70,0.3)] [&_svg]:size-[17px]"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M4 6.5C4 5 5 4 6.5 4h11C19 4 20 5 20 6.5v11c0 1.5-1 2.5-2.5 2.5h-11C5 20 4 19 4 17.5v-11Z"
              stroke="#fff"
              stroke-width="1.6"
            />
            <path
              d="m10 9 5 3-5 3V9Z"
              fill="#fff"
            />
          </svg>
        </div>
        <div>
          <div class="font-serif text-[17px] font-semibold leading-[1.1] tracking-[-0.01em]">Channel Analyzer</div>
          <div class="mt-[3px] font-mono text-[10.5px] uppercase tracking-[0.14em] text-dim-2">DeepVision · Audit</div>
        </div>
      </div>
    </div>

    <nav class="flex flex-col gap-[2px] px-3 py-2.5">
      <RouterLink
        v-for="item in NAV"
        :key="item.step"
        :to="item.to"
        :data-testid="`sidebar-nav-${item.step}`"
        class="relative flex w-full items-center gap-[11px] rounded-sm px-3 py-2.5 text-left text-[13.5px] font-medium no-underline transition-all duration-150"
        :class="
          route.meta.step === item.step
            ? `bg-popover text-foreground before:absolute before:bottom-[9px] before:left-0 before:top-[9px]
               before:w-[3px] before:rounded-[2px] before:bg-primary before:content-['']`
            : 'text-muted-foreground hover:bg-popover hover:text-foreground'
        "
      >
        <span
          class="w-4 font-mono text-[11px]"
          :class="route.meta.step === item.step ? 'text-primary' : 'text-dim-2'"
          >{{ item.num }}</span
        >
        {{ item.label }}
        <span
          v-if="item.step === 'collect'"
          class="ml-auto rounded-full bg-border px-[7px] py-[2px] font-mono text-[10px] text-muted-foreground"
          >{{ figBadge }}</span
        >
        <span
          v-else-if="item.step === 'audit'"
          class="ml-auto rounded-full bg-border px-[7px] py-[2px] font-mono text-[10px] text-muted-foreground"
          >{{ auditBadge }}</span
        >
      </RouterLink>
    </nav>

    <nav
      v-if="auth.isSuperadmin"
      class="flex flex-col gap-[2px] border-t px-3 py-2.5"
    >
      <RouterLink
        to="/users"
        data-testid="sidebar-nav-users"
        class="relative flex w-full items-center gap-[11px] rounded-sm px-3 py-2.5 text-left text-[13.5px] font-medium no-underline transition-all duration-150"
        :class="
          isUsersActive
            ? `bg-popover text-foreground before:absolute before:bottom-[9px] before:left-0 before:top-[9px]
               before:w-[3px] before:rounded-[2px] before:bg-primary before:content-['']`
            : 'text-muted-foreground hover:bg-popover hover:text-foreground'
        "
      >
        <span
          class="grid w-4 place-items-center [&_svg]:size-[15px]"
          :class="isUsersActive ? 'text-primary' : 'text-dim-2'"
        >
          <Users :stroke-width="1.6" />
        </span>
        Users
      </RouterLink>
    </nav>

    <SavedAuditList />

    <div class="mt-auto flex flex-col gap-2 border-t px-4 py-3.5">
      <Button
        data-testid="sidebar-new-audit"
        size="sm"
        @click="newAudit"
      >
        <Plus />
        New audit
      </Button>
      <Button
        variant="ghost"
        size="sm"
        @click="loadDemo"
      >
        Load demo channel
      </Button>
    </div>

    <div
      v-if="auth.user"
      class="flex items-center gap-2 border-t px-3 py-2.5"
      data-testid="sidebar-user"
    >
      <RouterLink
        to="/account"
        data-testid="sidebar-account-link"
        class="flex min-w-0 flex-1 flex-col rounded-sm px-2 py-1.5 no-underline transition-colors duration-150 hover:bg-popover"
      >
        <span class="truncate text-[13px] font-medium text-foreground">{{ auth.user.fullName }}</span>
        <span class="truncate font-mono text-[10.5px] text-dim-2">{{ auth.user.email }}</span>
      </RouterLink>
      <button
        type="button"
        title="Log out"
        aria-label="Log out"
        data-testid="sidebar-logout"
        class="grid size-8 flex-none place-items-center rounded-sm text-muted-foreground transition-colors duration-150 hover:bg-popover hover:text-foreground [&_svg]:size-[17px]"
        @click="logout"
      >
        <LogOut :stroke-width="1.7" />
      </button>
    </div>
  </aside>
</template>
